import { useEffect, useState, useMemo } from 'react';
import { adminExpenseApi } from '../../lib/api.js';
import { useToast } from '../../hooks/useToast.jsx';
import DataTable from '../../components/ui/DataTable.jsx';
import Button from '../../components/ui/Button.jsx';
import Input from '../../components/ui/Input.jsx';
import Modal from '../../components/ui/Modal.jsx';
import Badge from '../../components/ui/Badge.jsx';
import { Plus, Pencil, Trash2 } from 'lucide-react';

export default function ExpenseTypesPage() {
  const toast = useToast();
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editingType, setEditingType] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const res = await adminExpenseApi.listExpenseTypes();
      setTypes(res.types || []);
    } catch (err) {
      toast.error(`Failed to load expense types: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    if (!search) return types;
    const s = search.toLowerCase();
    return types.filter((t) => t.displayName?.toLowerCase().includes(s) || t.rowKey?.toLowerCase().includes(s));
  }, [types, search]);

  const handleSave = async (typeId, data) => {
    try {
      await adminExpenseApi.upsertExpenseType(typeId, data);
      toast.success(`Expense type "${data.displayName}" saved`);
      setEditingType(null);
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDelete = async (typeId) => {
    if (!confirm('Deactivate this expense type?')) return;
    try {
      await adminExpenseApi.deleteExpenseType(typeId);
      toast.success('Expense type deactivated');
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const columns = [
    {
      key: 'rowKey',
      label: 'ID',
      render: (t) => <span className="font-mono text-xs">{t.rowKey}</span>,
    },
    {
      key: 'displayName',
      label: 'Name',
      render: (t) => <span className="font-medium">{t.displayName}</span>,
    },
    {
      key: 'isActive',
      label: 'Status',
      render: (t) => (
        <Badge variant={t.isActive !== false ? 'success' : 'default'}>
          {t.isActive !== false ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    {
      key: 'actions',
      label: '',
      render: (t) => (
        <div className="flex items-center gap-1 justify-end">
          <button
            onClick={(e) => { e.stopPropagation(); setEditingType(t); }}
            className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
          {t.isActive !== false && (
            <button
              onClick={(e) => { e.stopPropagation(); handleDelete(t.rowKey); }}
              className="p-1.5 rounded-lg text-muted-foreground hover:text-profun-pink hover:bg-profun-pink/10 transition-colors"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Expense Types</h1>
          <p className="text-muted-foreground">{types.length} types configured</p>
        </div>
        <Button onClick={() => setEditingType({ rowKey: '', displayName: '', isActive: true, isNew: true })}>
          <Plus className="h-4 w-4 mr-2" />
          Add Type
        </Button>
      </div>

      <div className="mb-4">
        <Input
          placeholder="Search types..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-[300px]"
        />
      </div>

      <DataTable
        columns={columns}
        rows={filtered}
        loading={loading}
        emptyMessage="No expense types found."
        keyFn={(t) => t.rowKey}
      />

      {/* Edit/Create modal */}
      {editingType && (
        <ExpenseTypeModal
          type={editingType}
          onClose={() => setEditingType(null)}
          onSave={handleSave}
        />
      )}
    </div>
  );
}

function ExpenseTypeModal({ type, onClose, onSave }) {
  const [typeId, setTypeId] = useState(type.rowKey || '');
  const [displayName, setDisplayName] = useState(type.displayName || '');
  const [isActive, setIsActive] = useState(type.isActive !== false);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!typeId.trim() || !displayName.trim()) return;
    setSaving(true);
    await onSave(typeId.trim().toLowerCase().replace(/\s+/g, '-'), { displayName, isActive });
    setSaving(false);
  };

  return (
    <Modal isOpen onClose={onClose} title={type.isNew ? 'Add Expense Type' : 'Edit Expense Type'}>
      <div className="space-y-4">
        <Input
          label="Type ID (slug)"
          value={typeId}
          onChange={(e) => setTypeId(e.target.value)}
          placeholder="e.g. hotel, airfare, car-rental"
          disabled={!type.isNew}
        />
        <Input
          label="Display Name"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder="e.g. Hotel, Airfare / Ticketing"
        />
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            className="rounded border-white/20"
          />
          <span className="text-sm">Active</span>
        </label>
        <div className="flex justify-end gap-3 pt-2">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving || !typeId.trim() || !displayName.trim()}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
